import clsx from 'clsx'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  useGetByTenantAccountInprogressQuery,
  useGetByTenantRiskmanagementLatestHoldingstransferbalancesreconciliationQuery,
  usePostByTenantAccountConfirmAndAccountIdentifierMutation
} from '@/api/rtk-api'
import { Alert, Card, FieldOnly, OperationButton, ToastNotification } from '@/components/core-v2'
import { required } from '@/components/core-v2/molecules/fields/validators'
import { TENANT } from '@/const'
import { useUser } from '@/utilities/hooks/useUser'

import { UnitTransfersAsset } from '../unit-transfers-grid/unit-transfers-asset'
import { UnitTransfersContext } from '../unit-transfers-grid/unit-transfers-context'
import { UnitTransfersPrices } from '../unit-transfers-grid/unit-transfers-prices'
import { UnitTransfersTrades } from '../unit-transfers-grid/unit-transfers-trades'
import { mapData } from '../unit-transfers-utilities/map-data'
import { UnitTransfersLoading } from './unit-transfers-loading'
import classes from './unit-transfers-main.module.scss'

const getTradeCheckBoxIds = (ids: string[]) => ids.map(x => `${x}-checkbox`)

const addId = (assets: string[], id: string) => {
  const index = assets.indexOf(id)
  return index === -1 ? [...assets, id] : assets
}

const removeId = (assets: string[], id: string) => {
  const index = assets.indexOf(id)
  if (index === -1) return assets
  assets.splice(index, 1)
  return [...assets]
}

interface ReducerState {
  completed: string[]
  invalid: string[]
}

const defaultState: ReducerState = {
  completed: [],
  invalid: []
}

export enum ReducerActionType {
  Partial = 'partial',
  Completed = 'completed',
  Invalid = 'invalid'
}

const reducer = (state: ReducerState, action: { id: string; type: ReducerActionType }) => {
  const { completed, invalid } = state
  const { id } = action
  switch (action.type) {
    case ReducerActionType.Partial: {
      return {
        completed: removeId(completed, id),
        invalid: removeId(invalid, id)
      }
    }
    case ReducerActionType.Completed: {
      return {
        completed: addId(completed, id),
        invalid: removeId(invalid, id)
      }
    }
    case ReducerActionType.Invalid: {
      return {
        completed: removeId(completed, id),
        invalid: addId(invalid, id)
      }
    }
  }
}

export const UnitTransfersMain = () => {
  const [showToast, setShowToast] = useState(false)
  const { data: user } = useUser()
  const userName = user?.userName ?? 'administrator'

  const [mutate, { isLoading: isSubmitLoading, isSuccess }] =
    usePostByTenantAccountConfirmAndAccountIdentifierMutation()

  const { data, isLoading } = useGetByTenantAccountInprogressQuery({
    tenant: TENANT,
    pageSize: 1000
  })

  const { assets, trades } = mapData(data?.data ?? [])

  const { data: finswitch } =
    useGetByTenantRiskmanagementLatestHoldingstransferbalancesreconciliationQuery({
      tenant: TENANT
    })

  const form = useForm()
  const usernameFieldValue = form.watch('username')
  const checkBoxIds = getTradeCheckBoxIds([...trades.keys()])
  const checkBoxValues = form.watch(checkBoxIds)

  const [{ completed, invalid }, dispatch] = useReducer(reducer, defaultState)
  const contextValue = useMemo(() => ({ completed, dispatch }), [completed])

  useEffect(() => {
    if (isSuccess) {
      setShowToast(true)
    }
  }, [isSuccess])
  const onSubmit = data => {
    checkBoxValues.forEach((value, index) => {
      if (value) {
        const tradeId = [...trades.keys()][index]
        const transferOfUnits = trades.get(tradeId).transferUnits.map(x => ({
          assetId: x.assetId,
          originalPurchasePrice: data[`${tradeId}-${x.assetId}-price`].numerical / 100,
          units: data[`${tradeId}-${x.assetId}-unit`].numerical
        }))
        mutate({
          tenant: TENANT,
          accountIdentifier: tradeId,
          apiConfirmSettlementRequest: { transferOfUnits }
        })
      }
    })
  }

  if (isLoading) {
    return <UnitTransfersLoading />
  }

  if (trades.size === 0)
    return (
      <main className={clsx(classes.noTrades)}>
        <strong>0 trades available</strong>
      </main>
    )

  return (
    <main className={clsx(classes.main)}>
      <Card.Wrapper className={classes.cardWrapper}>
        <UnitTransfersContext.Provider value={contextValue}>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className={clsx(classes.gridWrapper)}>
                <UnitTransfersTrades trades={trades} />
                <div className={clsx(classes.scrollWrapper)}>
                  <div className={clsx(classes.assetWrapper)}>
                    {[...assets.values()].map(a => (
                      <UnitTransfersAsset key={a.assetId} asset={a} finswitch={finswitch} />
                    ))}
                  </div>
                  <UnitTransfersPrices trades={trades} />
                </div>
              </div>
              {invalid.map(x => {
                const { assetName } = assets.get(x)
                return (
                  <Alert key={x} className={clsx(classes.alert, `mx-4`, `mt-4`)} type="error">
                    There are insufficient units available on Finswitch for {assetName}
                  </Alert>
                )
              })}
              <div className={clsx(classes.note)}>
                Note: to reject a trade, please go to the trade in the 'Trade Builder' section
              </div>
              <div data-private className={clsx(classes.userNameText)}>
                To approve the settlement, enter your username '{userName}' below and click on the
                button
              </div>
              <FieldOnly
                data-private
                className={clsx('mb-4', 'ml-4', classes.userNameField)}
                id="username"
                type="text"
                validators={required}
              />
              <OperationButton
                className="ml-4 mb-4"
                isDisabled={
                  isLoading || usernameFieldValue !== userName || checkBoxValues.every(x => !x)
                }
                isLoading={isLoading || isSubmitLoading}
                operationType="progress"
                type="submit"
              >
                Submit
              </OperationButton>
            </form>
            <ToastNotification
              description="confirmed units for checked trades"
              notificationType="success"
              open={showToast}
              title="Confirmed Unit Transfers"
              onClose={() => {
                setShowToast(false)
              }}
            />
          </FormProvider>
        </UnitTransfersContext.Provider>
      </Card.Wrapper>
    </main>
  )
}
